{% block uc_milestone_manager %}
<div class="uc-milestone-manager">

    {# ── Toolbar ── #}
    <div class="uc-milestone-manager__toolbar">
        <p class="uc-milestone-manager__hint">
            {{ $tc('sellops-ultimate-cart.milestones.hint') }}
        </p>
        <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" variant="primary" size="small" @click="openCreate">
            {{ $tc('sellops-ultimate-cart.milestones.addButton') }}
        </sw-button>
    </div>

    {# ── Loading ── #}
    <sw-loader v-if="isLoading" />

    {# ── Load error — distinct from "no milestones yet" so a failed request
         never reads as "your milestones were deleted" ── #}
    <sw-alert v-else-if="loadError" variant="error">
        <strong>{{ $tc('sellops-ultimate-cart.milestones.loadErrorTitle') }}</strong>
        <p>{{ $tc('sellops-ultimate-cart.milestones.loadErrorSubline') }}</p>
    </sw-alert>

    {# ── Empty state ── #}
    <div v-else-if="!sortedMilestones.length" class="uc-milestone-manager__empty">
        <p>{{ $tc('sellops-ultimate-cart.milestones.emptyTitle') }}</p>
        <p class="uc-milestone-manager__hint">{{ $tc('sellops-ultimate-cart.milestones.emptySubline') }}</p>
    </div>

    {# ── Milestone table ── #}
    <template v-else>
        <table class="uc-milestone-manager__table">
            <thead>
                <tr>
                    <th>{{ $tc('sellops-ultimate-cart.milestones.colPosition') }}</th>
                    <th>{{ $tc('sellops-ultimate-cart.milestones.colThreshold') }}</th>
                    <th>{{ $tc('sellops-ultimate-cart.milestones.colReward') }}</th>
                    <th>{{ $tc('sellops-ultimate-cart.milestones.colLabelBefore') }}</th>
                    <th>{{ $tc('sellops-ultimate-cart.milestones.colActive') }}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="milestone in sortedMilestones"
                    :key="milestone.id"
                    :class="{ 'is-inactive': !milestone.active }"
                >
                    <td class="uc-milestone-manager__pos">
                        <span class="uc-milestone-manager__badge">{{ milestone.position }}</span>
                    </td>
                    <td class="uc-milestone-manager__threshold">
                        <strong>{{ milestone.threshold }}</strong>
                    </td>
                    <td class="uc-milestone-manager__reward">
                        <sw-label
                            :variant="milestone.rewardType === 'free_shipping' ? 'success' : 'info'"
                            size="small"
                        >
                            {{ rewardLabel(milestone) }}
                        </sw-label>

                        {# Compact indicator for the chosen celebration animation
                           — only shown once one is actually picked, so a plain
                           "none" milestone's row stays uncluttered. #}
                        <sw-label
                            v-if="milestone.animationType && milestone.animationType !== 'none'"
                            variant="neutral"
                            size="small"
                            :title="$tc('sellops-ultimate-cart.milestones.animationType.' + milestone.animationType)"
                        >
                            ✨ {{ $tc('sellops-ultimate-cart.milestones.animationType.' + milestone.animationType) }}
                        </sw-label>

                        {# Warning for a saved milestone whose linked promotion
                           currently won't apply — expired, disabled, code was
                           turned back on, or unassigned from every sales
                           channel. Prevents the "why isn't this showing up"
                           surprise the merchant would otherwise only discover
                           by checking the storefront. #}
                        <sw-label
                            v-if="milestonePromotionStatus(milestone)"
                            variant="danger"
                            size="small"
                            :title="promotionStatusLabel(milestonePromotionStatus(milestone))"
                            class="uc-milestone-manager__promotion-warning"
                        >
                            ⚠ {{ $tc('sellops-ultimate-cart.milestones.statusWarningShort') }}
                        </sw-label>

                        {# Same combination-conflict check as the form, kept
                           visible here so it doesn't take re-opening every
                           milestone to notice one promotion is silently
                           losing to another. #}
                        <sw-label
                            v-for="conflict in milestonePromotionConflicts(milestone)"
                            :key="'conflict-' + conflict.milestone.id"
                            :variant="conflict.outcome === 'wins' ? 'warning' : 'danger'"
                            size="small"
                            :title="conflictMessage(conflict)"
                            class="uc-milestone-manager__promotion-warning"
                        >
                            ⚠ {{ $tc('sellops-ultimate-cart.milestones.conflictBadgeShort') }}
                        </sw-label>
                    </td>
                    <td class="uc-milestone-manager__label-before">
                        <span class="uc-milestone-manager__label-text">{{ milestone.labelBefore }}</span>
                    </td>
                    <td class="uc-milestone-manager__active">
                        <span :class="milestone.active ? 'uc-active-yes' : 'uc-active-no'">
                            {{ milestone.active ? '✓' : '✗' }}
                        </span>
                    </td>
                    <td class="uc-milestone-manager__actions">
                        <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" size="x-small" @click="openEdit(milestone)">
                            {{ $tc('sellops-ultimate-cart.milestones.editButton') }}
                        </sw-button>
                        <sw-button v-if="acl.can('sellops_ultimate_cart.deleter')" size="x-small" variant="danger" @click="confirmDelete(milestone.id)">
                            {{ $tc('global.default.delete') }}
                        </sw-button>
                    </td>
                </tr>
            </tbody>
        </table>
    </template>

    {# ── Create / Edit modal ── #}
    <sw-modal
        v-if="showModal"
        :title="isNewMilestone
            ? $tc('sellops-ultimate-cart.milestones.modalTitleCreate')
            : $tc('sellops-ultimate-cart.milestones.modalTitleEdit')"
        @modal-close="cancelModal"
    >
        <div class="uc-milestone-manager__form">

            {# Row 1: Position + Threshold #}
            <div class="uc-milestone-manager__form-row">
                <sw-number-field
                    :value="form.position"
                    :label="$tc('sellops-ultimate-cart.milestones.fieldPosition')"
                    :min="1"
                    :step="1"
                    number-type="int"
                    @change="form.position = $event"
                />
                <sw-number-field
                    :value="form.threshold"
                    :label="$tc('sellops-ultimate-cart.milestones.fieldThreshold')"
                    :min="0"
                    :step="0.01"
                    @change="form.threshold = $event"
                />
            </div>

            {# Reward type — Shopware 6.7 sw-single-select: value prop + update:value event #}
            <sw-single-select
                :value="form.rewardType"
                :label="$tc('sellops-ultimate-cart.milestones.fieldRewardType')"
                :options="rewardTypeOptions"
                @update:value="form.rewardType = $event"
            />

            {# Free shipping setup notice — shown when merchant selects free_shipping #}
            <sw-alert
                v-if="form.rewardType === 'free_shipping'"
                variant="info"
                :showIcon="true"
                class="uc-milestone-manager__free-shipping-notice"
            >
                <strong>{{ $tc('sellops-ultimate-cart.milestones.freeShippingNoticeTitle') }}</strong>
                <p class="uc-milestone-manager__notice-para">{{ $tc('sellops-ultimate-cart.milestones.freeShippingNoticeText') }}</p>
                <ol class="uc-milestone-manager__notice-list">
                    <li>{{ $tc('sellops-ultimate-cart.milestones.freeShippingStep1') }}</li>
                    <li>{{ $tc('sellops-ultimate-cart.milestones.freeShippingStep2') }}</li>
                    <li>{{ $tc('sellops-ultimate-cart.milestones.freeShippingStep3') }}</li>
                    <li>{{ $tc('sellops-ultimate-cart.milestones.freeShippingStep4') }}</li>
                </ol>
            </sw-alert>

            {# Reward source — only for discount types: type a value, or pick
               an existing (automatic, codeless) Shopware Promotion #}
            <sw-single-select
                v-if="showRewardValue"
                :value="rewardSource"
                :label="$tc('sellops-ultimate-cart.milestones.fieldRewardSource')"
                :options="rewardSourceOptions"
                @update:value="rewardSource = $event"
            />

            {# Custom value #}
            <sw-number-field
                v-if="showRewardValue && rewardSource === 'custom'"
                :value="form.rewardValue"
                :label="rewardValueLabel"
                :min="0"
                :step="0.01"
                @change="form.rewardValue = $event"
            />

            {# Existing Promotion #}
            <template v-if="showRewardValue && rewardSource === 'promotion'">
                <sw-entity-single-select
                    entity="promotion"
                    :value="form.promotionId"
                    :criteria="promotionCriteria"
                    :label="$tc('sellops-ultimate-cart.milestones.fieldPromotion')"
                    :placeholder="$tc('sellops-ultimate-cart.milestones.fieldPromotionPlaceholder')"
                    :help-text="$tc('sellops-ultimate-cart.milestones.fieldPromotionHelp')"
                    @update:value="onPromotionSelected"
                />

                {# Live eligibility check for whichever promotion is picked —
                   same check the storefront applies before showing the reward,
                   surfaced here so the merchant finds out immediately instead
                   of wondering later why the milestone isn't showing up. #}
                <sw-loader v-if="form.promotionId && isLoadingPromotionDetail" size="16px" />

                <template v-else-if="form.promotionId">
                    {# Eligibility status — own independent v-if/v-else-if chain
                       so it can never be swallowed by the threshold-detection
                       notes below (they answer a different question). #}
                    <sw-alert
                        v-if="selectedPromotionStatus"
                        variant="danger"
                        :showIcon="true"
                    >
                        <strong>{{ $tc('sellops-ultimate-cart.milestones.statusWarningTitle') }}</strong>
                        <p class="uc-milestone-manager__status-detail">{{ promotionStatusLabel(selectedPromotionStatus) }}</p>
                    </sw-alert>

                    <sw-alert
                        v-else-if="isFixedCodePromotion(selectedPromotionDetail)"
                        variant="success"
                        :showIcon="true"
                    >
                        {{ $tc('sellops-ultimate-cart.milestones.statusOkFixedCode') }}
                    </sw-alert>

                    <sw-alert
                        v-else
                        variant="success"
                        :showIcon="true"
                    >
                        {{ $tc('sellops-ultimate-cart.milestones.statusOk') }}
                    </sw-alert>

                    {# Cart-total threshold detection — a separate concern from
                       eligibility, shown alongside it rather than instead of it. #}
                    <sw-alert
                        v-if="detectedThreshold !== null"
                        variant="info"
                        :showIcon="true"
                    >
                        {{ $tc('sellops-ultimate-cart.milestones.thresholdDetectedNote') }} {{ detectedThreshold }}
                    </sw-alert>

                    <sw-alert
                        v-else
                        variant="info"
                        :showIcon="true"
                    >
                        {{ $tc('sellops-ultimate-cart.milestones.thresholdNotDetectedNote') }}
                    </sw-alert>

                    {# Combination conflicts with another milestone's promotion —
                       a separate concern from single-promotion eligibility
                       above, so it's its own loop rather than chained into
                       that v-if/v-else-if chain. See promotionConflicts(). #}
                    <sw-alert
                        v-for="conflict in selectedPromotionConflicts"
                        :key="conflict.milestone.id"
                        :variant="conflict.outcome === 'wins' ? 'warning' : 'danger'"
                        :showIcon="true"
                    >
                        <strong>{{ $tc('sellops-ultimate-cart.milestones.conflictTitle') }}</strong>
                        <p class="uc-milestone-manager__status-detail">{{ conflictMessage(conflict) }}</p>
                    </sw-alert>
                </template>

                <sw-alert
                    variant="info"
                    :showIcon="true"
                    class="uc-milestone-manager__free-shipping-notice"
                >
                    <strong>{{ $tc('sellops-ultimate-cart.milestones.promotionNoticeTitle') }}</strong>
                    <p class="uc-milestone-manager__notice-para">{{ $tc('sellops-ultimate-cart.milestones.promotionNoticeText') }}</p>
                    <ol class="uc-milestone-manager__notice-list">
                        <li>{{ $tc('sellops-ultimate-cart.milestones.promotionStepAuto') }}</li>
                        <li>{{ $tc('sellops-ultimate-cart.milestones.promotionStepFixed') }}</li>
                    </ol>
                </sw-alert>
            </template>


            {# Icon — same media-library picker as the trust bar's custom
               badges (config.xml), instead of a free-text URL field: pick or
               upload an image, no arbitrary URL typing. Stored value is a
               media UUID; ProgressBarResolver resolves it to a real URL for
               the storefront (and keeps rejecting anything that isn't a
               genuine http(s) URL for milestones saved before this change). #}
            <uc-media-upload
                :value="form.iconUrl"
                :label="$tc('sellops-ultimate-cart.milestones.fieldIcon')"
                :help-text="$tc('sellops-ultimate-cart.milestones.fieldIconHelp')"
                @update:value="form.iconUrl = $event"
            />

            {# Labels #}
            <sw-text-field
                :value="form.labelBefore"
                :label="$tc('sellops-ultimate-cart.milestones.fieldLabelBefore')"
                :placeholder="$tc('sellops-ultimate-cart.milestones.fieldLabelBeforePlaceholder', 0, { token: '{remaining}' })"
                :help-text="$tc('sellops-ultimate-cart.milestones.fieldLabelBeforeHelp', 0, { token: '{remaining}' })"
                @update:value="form.labelBefore = $event"
            />
            <sw-text-field
                :value="form.labelAfter"
                :label="$tc('sellops-ultimate-cart.milestones.fieldLabelAfter')"
                :placeholder="$tc('sellops-ultimate-cart.milestones.fieldLabelAfterPlaceholder')"
                @update:value="form.labelAfter = $event"
            />

            {# Celebration animation — plays once, client-side, the moment a
               shopper crosses this milestone. Purely cosmetic; doesn't affect
               the reward itself. See docs/celebration-animations-architecture.md. #}
            <sw-single-select
                :value="form.animationType"
                :label="$tc('sellops-ultimate-cart.milestones.fieldAnimationType')"
                :help-text="$tc('sellops-ultimate-cart.milestones.fieldAnimationTypeHelp')"
                :options="animationTypeOptions"
                @update:value="form.animationType = $event"
            />

            {# Active toggle #}
            <sw-switch-field
                :value="form.active"
                :label="$tc('sellops-ultimate-cart.milestones.fieldActive')"
                bordered
                @update:value="form.active = $event"
            />

        </div>

        <template #modal-footer>
            <sw-button @click="cancelModal" :disabled="isSaving">
                {{ $tc('global.default.cancel') }}
            </sw-button>
            <sw-button v-if="acl.can('sellops_ultimate_cart.editor')" variant="primary" :isLoading="isSaving" @click="saveMilestone">
                {{ $tc('global.default.save') }}
            </sw-button>
        </template>
    </sw-modal>

    {# ── Delete confirmation modal ── #}
    <sw-modal
        v-if="showDeleteConfirm"
        :title="$tc('sellops-ultimate-cart.milestones.deleteConfirmTitle')"
        variant="small"
        @modal-close="cancelDelete"
    >
        <p>{{ $tc('sellops-ultimate-cart.milestones.deleteConfirmText') }}</p>

        <template #modal-footer>
            <sw-button @click="cancelDelete" :disabled="isDeleting">
                {{ $tc('global.default.cancel') }}
            </sw-button>
            <sw-button v-if="acl.can('sellops_ultimate_cart.deleter')" variant="danger" :isLoading="isDeleting" @click="executeDelete">
                {{ $tc('global.default.delete') }}
            </sw-button>
        </template>
    </sw-modal>

</div>
{% endblock %}
